import React, { useState, useEffect } from 'react';
import { Settings, LogOut, ChevronRight, LayoutGrid } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { resolveAssetUrl } from '../../config/api';

interface UserProfileMenuProps {
  isExpanded: boolean;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
}

const UserProfileMenu: React.FC<UserProfileMenuProps> = ({ isExpanded, onMenuOpen, onMenuClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);

  // Close menu when sidebar collapses
  useEffect(() => {
    if (!isExpanded) {
      setShowMenu(false);
    }
  }, [isExpanded]);

  // Notify parent when menu opens or closes
  useEffect(() => {
    if (showMenu && onMenuOpen) {
      onMenuOpen();
    } else if (!showMenu && onMenuClose) {
      onMenuClose();
    }
  }, [showMenu, onMenuOpen, onMenuClose]);

  const handleMenuAction = async (value: string) => {
    setShowMenu(false);
    
    switch (value) {
      case 'modules':
        navigate('/modules');
        break;
      case 'settings':
        navigate('/settings');
        break;
      case 'logout':
        await logout(); // This will redirect to /login automatically
        break;
    }
  };

  const getInitials = (name: string | null) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300';
      case 'user': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300';
      case 'viewer': return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
      default: return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  if (!user) {
    return (
      <div className="p-3 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        className={`w-full p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-3 rounded-xl group ${
          !isExpanded ? 'justify-center' : ''
        }`}
      >
        {/* Avatar */}
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold text-sm shadow-md flex-shrink-0">
          {user.avatar ? (
            <img src={resolveAssetUrl(user.avatar)} alt={user.name || 'User'} className="w-full h-full rounded-xl object-cover" />
          ) : (
            getInitials(user.name)
          )}
        </div>
        
        {/* User Info (when expanded) */}
        {isExpanded && (
          <div className="flex-1 text-left min-w-0">
            <p 
              className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate"
              style={{ animation: 'fadeInText 200ms ease-in 150ms both' }}
            >
              {user.name}
            </p>
            <span 
              className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}
              style={{ animation: 'fadeInText 200ms ease-in 150ms both' }}
            >
              {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
            </span>
          </div>
        )}

        {/* Chevron (when expanded) */}
        {isExpanded && (
          <ChevronRight 
            className={`h-4 w-4 text-gray-400 dark:text-slate-400 group-hover:text-gray-600 dark:group-hover:text-slate-300 flex-shrink-0 transition-transform duration-200 ${
              showMenu ? '-rotate-90' : 'rotate-0'
            }`}
          />
        )}
      </button>

      {/* Dropdown Menu */}
      {showMenu && isExpanded && (
        <div className="absolute bottom-full left-0 w-full mb-2 animate-slideDown">
          <div className="bg-white dark:bg-dark-surface border-2 border-gray-300 dark:border-dark-border rounded-xl shadow-2xl overflow-hidden">
            {/* User info header */}
            <div className="p-3 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 border-b border-gray-200 dark:border-dark-border">
              <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{user.name}</p>
              <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
              </span>
            </div>
            
            {/* Menu options */}
            <button
              onClick={() => handleMenuAction('modules')}
              className="w-full px-4 py-3 text-left hover:bg-purple-50 dark:hover:bg-purple-900/20 flex items-center gap-3 text-sm transition-colors border-b border-gray-200 dark:border-dark-border"
            >
              <LayoutGrid className="h-4 w-4 text-purple-500 dark:text-purple-400" />
              <span className="text-gray-700 dark:text-gray-300 font-medium">Modules</span>
            </button>
            
            <button
              onClick={() => handleMenuAction('settings')}
              className="w-full px-4 py-3 text-left hover:bg-blue-50 dark:hover:bg-blue-900/20 flex items-center gap-3 text-sm transition-colors border-b border-gray-200 dark:border-dark-border"
            >
              <Settings className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              <span className="text-gray-700 dark:text-gray-300 font-medium">Settings</span>
            </button>
            
            <button
              onClick={() => handleMenuAction('logout')}
              className="w-full px-4 py-3 text-left hover:bg-rose-50 dark:hover:bg-rose-900/20 flex items-center gap-3 text-sm transition-colors"
            >
              <LogOut className="h-4 w-4 text-rose-500 dark:text-rose-400" />
              <span className="text-rose-700 dark:text-rose-400 font-medium">Logout</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileMenu;

